import { isKnownFruitProductId } from "../init-game";
import {
  type ConfirmRoundResultAction,
  FRUIT_PRICE_WON_VALUES,
  type FruitPricesByProduct,
  type FruitProductId,
  type FruitShopV2Action,
  type FruitShopV2Player,
  type FruitShopV2State,
  type ReadyForPriceSubmissionAction,
  type RevealRoundAction,
  type StartExtensionRoundAction,
  type SubmitPricesAction,
  type UseChangeAction,
  type UseSecretAction,
  type ValidationResult,
} from "../types";

const VALID_PRICE_SET = new Set<number>(FRUIT_PRICE_WON_VALUES);
const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function findPlayer(
  state: FruitShopV2State,
  playerId: string,
): FruitShopV2Player | undefined {
  return state.players.find((player) => player.playerId === playerId);
}

function hasPlayer(state: FruitShopV2State, playerId: string): boolean {
  return state.players.some((player) => player.playerId === playerId);
}

function isHostAction(state: FruitShopV2State, playerId: string): boolean {
  return state.hostPlayerId === playerId;
}

function hasOnlyKnownProducts(values: Readonly<Record<string, unknown>>) {
  return Object.keys(values).every(isKnownFruitProductId);
}

function validatePricesForPlayer(
  player: FruitShopV2Player,
  prices: FruitPricesByProduct,
): ValidationResult {
  if (!hasOnlyKnownProducts(prices)) {
    return fail("Unknown fruit in price submission");
  }

  const submittedProductIds = Object.keys(prices) as FruitProductId[];
  const missingProductIds = player.fruitRights.filter(
    (productId) => prices[productId] === undefined,
  );
  const extraProductIds = submittedProductIds.filter(
    (productId) => !player.fruitRights.includes(productId),
  );

  if (missingProductIds.length > 0 || extraProductIds.length > 0) {
    return fail(
      "Submitted prices must match the player's current fruit rights",
      {
        missingProductIds,
        extraProductIds,
      },
    );
  }

  for (const productId of player.fruitRights) {
    const priceWon = prices[productId];
    if (priceWon === undefined || !VALID_PRICE_SET.has(priceWon)) {
      return fail("Price must be 1000-5000 won in 1000-won increments", {
        productId,
        priceWon,
      });
    }
  }

  return ok;
}

export function validateReadyForPriceSubmission(
  state: FruitShopV2State,
  action: ReadyForPriceSubmissionAction,
): ValidationResult {
  if (state.currentRound.phase !== "ITEM_WINDOW") {
    return fail("Readiness can only be marked during the item window");
  }

  if (!hasPlayer(state, action.playerId)) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  return ok;
}

export function validateSubmitPrices(
  state: FruitShopV2State,
  action: SubmitPricesAction,
): ValidationResult {
  if (state.currentRound.phase !== "PRICE_SUBMISSION") {
    return fail("Prices can only be submitted during price submission");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (state.currentRound.priceSubmissions[action.playerId]) {
    return fail("Player has already submitted prices for this round", {
      playerId: action.playerId,
    });
  }

  return validatePricesForPlayer(player, action.prices);
}

export function validateUseSecret(
  state: FruitShopV2State,
  action: UseSecretAction,
): ValidationResult {
  if (state.status !== "IN_PROGRESS") {
    return fail("Cannot use secret after the game has completed");
  }

  if (
    state.currentRound.phase !== "ITEM_WINDOW" &&
    state.currentRound.phase !== "PRICE_SUBMISSION"
  ) {
    return fail("Secret must be used before round reveal");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (player.itemStatus !== "AVAILABLE") {
    return fail("Player has already used their one-time item");
  }

  if (!isKnownFruitProductId(action.productId)) {
    return fail("Unknown secret target", { productId: action.productId });
  }

  return ok;
}

export function validateUseChange(
  state: FruitShopV2State,
  action: UseChangeAction,
): ValidationResult {
  if (state.status !== "IN_PROGRESS") {
    return fail("Cannot use change after the game has completed");
  }

  if (state.currentRound.phase !== "ITEM_WINDOW") {
    return fail("Change must be used before price submission starts");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (player.itemStatus !== "AVAILABLE") {
    return fail("Player has already used their one-time item");
  }

  if (
    !isKnownFruitProductId(action.fromProductId) ||
    !isKnownFruitProductId(action.toProductId)
  ) {
    return fail("Unknown change fruit target");
  }

  if (!player.fruitRights.includes(action.fromProductId)) {
    return fail("Player does not own the fruit right being changed", {
      fromProductId: action.fromProductId,
    });
  }

  if (player.fruitRights.includes(action.toProductId)) {
    return fail("Player cannot hold duplicate fruit rights", {
      toProductId: action.toProductId,
    });
  }

  return ok;
}

export function validateRevealRound(
  state: FruitShopV2State,
  action: RevealRoundAction,
): ValidationResult {
  if (!isHostAction(state, action.playerId)) {
    return fail("Only the host can reveal the round");
  }

  if (state.currentRound.phase !== "PRICE_SUBMISSION") {
    return fail("Round can only be revealed after price submission");
  }

  const missingSubmitterIds = state.players
    .map((player) => player.playerId)
    .filter((playerId) => !state.currentRound.priceSubmissions[playerId]);

  if (missingSubmitterIds.length > 0) {
    return fail("All players must submit prices before reveal", {
      missingSubmitterIds,
    });
  }

  return ok;
}

export function validateConfirmRoundResult(
  state: FruitShopV2State,
  action: ConfirmRoundResultAction,
): ValidationResult {
  if (state.currentRound.phase !== "ROUND_REVEALED") {
    return fail("Round results can only be confirmed after reveal");
  }

  if (!hasPlayer(state, action.playerId)) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  return ok;
}

export function validateStartExtensionRound(
  state: FruitShopV2State,
  action: StartExtensionRoundAction,
): ValidationResult {
  if (!isHostAction(state, action.playerId)) {
    return fail("Only the host can start an extension round");
  }

  if (state.currentRound.phase !== "EXTENSION_READY") {
    return fail("Extension can only start from extension-ready state");
  }

  return ok;
}

export function validateAction(
  state: FruitShopV2State,
  action: FruitShopV2Action,
): ValidationResult {
  if (action.type === "ReadyForPriceSubmission") {
    return validateReadyForPriceSubmission(state, action);
  }
  if (action.type === "SubmitPrices") {
    return validateSubmitPrices(state, action);
  }
  if (action.type === "UseSecret") {
    return validateUseSecret(state, action);
  }
  if (action.type === "UseChange") {
    return validateUseChange(state, action);
  }
  if (action.type === "RevealRound") {
    return validateRevealRound(state, action);
  }
  if (action.type === "ConfirmRoundResult") {
    return validateConfirmRoundResult(state, action);
  }
  if (action.type === "StartExtensionRound") {
    return validateStartExtensionRound(state, action);
  }
  return fail("Unknown action");
}
